import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { CLIENT_COLORS, PRESTATIONS } from "@/lib/constants";
import { toast } from "sonner";

const sel = "w-full border border-neutral-200 rounded-md px-2.5 py-2 text-sm bg-white";

const EMPTY = {
  name: "", sector: "", email: "", phone: "", color: CLIENT_COLORS[0],
  contact_first_name: "", contact_last_name: "", contact_function: "",
  raison_sociale: "", trade_name: "", address: "", zip_code: "", city: "", country: "France",
  siret: "", ide_che: "", vat_number: "", billing_email: "", billing_contact: "", billing_phone: "",
};

export default function NewClientModal({ open, setOpen, onCreated }) {
  const [f, setF] = useState(EMPTY);
  const [services, setServices] = useState(["community_management"]);
  const [respOpen, setRespOpen] = useState(false);
  const [billOpen, setBillOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    if (!f.name.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/clients", { ...f, name: f.name.trim(), email: f.email.trim(), services });
      toast.success(`Client « ${data.name} » créé`);
      setOpen(false); setF(EMPTY); setServices(["community_management"]); setRespOpen(false); setBillOpen(false);
      onCreated?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la création");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-client-modal" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading">Nouveau client</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Nom de l'entreprise *</Label>
            <Input data-testid="new-client-name" value={f.name} onChange={set("name")} placeholder="Ex : Brin de Beauté" className="mt-1.5" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Nom et prénom du client</Label>
              <Input data-testid="new-client-contact" value={f.contact_first_name} onChange={set("contact_first_name")} placeholder="Prénom" className="mt-1.5" />
            </div>
            <div>
              <Label>&nbsp;</Label>
              <Input data-testid="new-client-contact-last" value={f.contact_last_name} onChange={set("contact_last_name")} placeholder="Nom" className="mt-1.5" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Adresse e-mail principale</Label>
              <Input data-testid="new-client-email" type="email" value={f.email} onChange={set("email")} placeholder="contact@client.com" className="mt-1.5" />
            </div>
            <div>
              <Label>Numéro de téléphone</Label>
              <Input data-testid="new-client-phone" value={f.phone} onChange={set("phone")} placeholder="+33 …" className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label>Domaine d'activité</Label>
            <Input data-testid="new-client-sector" value={f.sector} onChange={set("sector")} placeholder="Ex : Institut de beauté" className="mt-1.5" />
          </div>
          <div>
            <Label>Prestations <span className="text-neutral-400 font-normal">(cumulables)</span></Label>
            <div className="grid grid-cols-2 gap-1.5 mt-1.5" data-testid="new-client-services">
              {PRESTATIONS.map((p) => (
                <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`new-client-service-${p.id}`}>
                  <input type="checkbox" className="rounded" checked={services.includes(p.id)}
                    onChange={(e) => setServices((s) => e.target.checked ? [...s, p.id] : s.filter((x) => x !== p.id))} />
                  {p.label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label>Couleur de la marque</Label>
            <div className="flex gap-2 mt-2">
              {CLIENT_COLORS.map((c) => (
                <button key={c} data-testid={`color-${c}`} onClick={() => set("color")({ target: { value: c } })}
                  className={`w-8 h-8 rounded-full transition-transform ${f.color === c ? "ring-2 ring-offset-2 ring-neutral-900 scale-110" : "hover:scale-105"}`}
                  style={{ background: c }} />
              ))}
            </div>
          </div>

          <div className="border border-neutral-200 rounded-lg" data-testid="new-client-resp-section">
            <button type="button" onClick={() => setRespOpen(!respOpen)} data-testid="new-client-resp-toggle"
              className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium text-left">
              Coordonnées du responsable <span className="text-xs text-neutral-400 font-normal">facultatif {respOpen ? "▴" : "▾"}</span>
            </button>
            {respOpen && (
              <div className="grid grid-cols-2 gap-3 px-3 pb-3">
                <div><Label className="text-xs">Fonction</Label>
                  <Input className="mt-1" value={f.contact_function} onChange={set("contact_function")} placeholder="Ex : Gérante" data-testid="new-client-resp-function" /></div>
                <div><Label className="text-xs">E-mail professionnel</Label>
                  <Input className="mt-1" type="email" value={f.billing_email && false ? "" : f.contact_email || ""} onChange={(e) => setF((p) => ({ ...p, contact_email: e.target.value }))} data-testid="new-client-resp-email" /></div>
              </div>
            )}
          </div>

          <div className="border border-neutral-200 rounded-lg" data-testid="new-client-billing-section">
            <button type="button" onClick={() => setBillOpen(!billOpen)} data-testid="new-client-billing-toggle"
              className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium text-left">
              Coordonnées de facturation <span className="text-xs text-neutral-400 font-normal">facultatif {billOpen ? "▴" : "▾"}</span>
            </button>
            {billOpen && (
              <div className="grid grid-cols-2 gap-3 px-3 pb-3">
                <div><Label className="text-xs">Raison sociale</Label>
                  <Input className="mt-1" value={f.raison_sociale} onChange={set("raison_sociale")} data-testid="new-client-raison-sociale" /></div>
                <div><Label className="text-xs">Nom commercial (si différent)</Label>
                  <Input className="mt-1" value={f.trade_name} onChange={set("trade_name")} data-testid="new-client-trade-name" /></div>
                <div className="col-span-2"><Label className="text-xs">Adresse de facturation</Label>
                  <Input className="mt-1" value={f.address} onChange={set("address")} data-testid="new-client-address" /></div>
                <div><Label className="text-xs">Code postal</Label>
                  <Input className="mt-1" value={f.zip_code} onChange={set("zip_code")} data-testid="new-client-zip" /></div>
                <div><Label className="text-xs">Ville</Label>
                  <Input className="mt-1" value={f.city} onChange={set("city")} data-testid="new-client-city" /></div>
                <div><Label className="text-xs">Pays</Label>
                  <select className={`${sel} mt-1`} value={f.country} onChange={set("country")} data-testid="new-client-country">
                    <option value="France">France</option><option value="Suisse">Suisse</option>
                    <option value="Belgique">Belgique</option><option value="Autre">Autre</option>
                  </select></div>
                {f.country === "Suisse" ? (
                  <div><Label className="text-xs">N° IDE / CHE</Label>
                    <Input className="mt-1" value={f.ide_che} onChange={set("ide_che")} placeholder="CHE-000.000.000" data-testid="new-client-ide" /></div>
                ) : (
                  <div><Label className="text-xs">N° SIREN / SIRET</Label>
                    <Input className="mt-1" value={f.siret} onChange={set("siret")} data-testid="new-client-siret" /></div>
                )}
                <div><Label className="text-xs">N° de TVA (si applicable)</Label>
                  <Input className="mt-1" value={f.vat_number} onChange={set("vat_number")} data-testid="new-client-vat" /></div>
                <div><Label className="text-xs">E-mail de facturation</Label>
                  <Input className="mt-1" type="email" value={f.billing_email} onChange={set("billing_email")} data-testid="new-client-billing-email" /></div>
                <div><Label className="text-xs">Responsable facturation (nom)</Label>
                  <Input className="mt-1" value={f.billing_contact} onChange={set("billing_contact")} data-testid="new-client-billing-contact" /></div>
                <div><Label className="text-xs">Tél. responsable facturation</Label>
                  <Input className="mt-1" value={f.billing_phone} onChange={set("billing_phone")} data-testid="new-client-billing-phone" /></div>
              </div>
            )}
          </div>

          <Button data-testid="new-client-submit" onClick={submit} disabled={loading || !f.name.trim()}
            className="w-full bg-neutral-900 hover:bg-neutral-800 text-white">
            {loading ? "Création…" : "Créer le client"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
